import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, X, CheckCircle2, AlertTriangle, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminRecoverMissingTickersButton — Round 93 (Jun 2026), per-row
 * controls added Round 96k (Jul 2026). Round P111u (Feb 2026) added
 * self-gating via useAuth so this button hides itself even if the
 * parent wrapper is bypassed (belt-and-suspenders).
 *
 * Scans `dividend_history_massive` for tickers not present in
 * `stock_cache` and lets the admin decide per-ticker:
 *   • ↻ Recover — re-runs the fetch pipeline for THIS ticker only
 *   • 🗑 Trash   — adds to the permanent dynamic blocklist so it never
 *                   shows up again + purges any residue from caches
 * The bulk "Recover all remaining" button is still available for the
 * common case where every missing ticker should just come back.
 */
export default function AdminRecoverMissingTickersButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [recoveryResult, setRecoveryResult] = useState(null);
  const [error, setError] = useState(null);
  // Per-row loading state so a click on one ticker doesn't disable the
  // whole modal — value is the ticker currently being worked on.
  const [busyTicker, setBusyTicker] = useState(null);
  // Tickers the admin resolved in this session (either recovered or
  // trashed). We strip them from the visible missing list so the modal
  // reflects the admin's actions live without a second dry-run.
  const [resolved, setResolved] = useState(() => ({ recovered: new Set(), trashed: new Set() }));

  const openModal = async () => {
    setOpen(true);
    setError(null);
    setRecoveryResult(null);
    setResolved({ recovered: new Set(), trashed: new Set() });
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/admin/recover-missing-dividend-tickers?dry_run=true`,
        null,
        { withCredentials: true }
      );
      setDryRunResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to scan');
    } finally {
      setLoading(false);
    }
  };

  // Recover a single ticker via the same endpoint but with a `tickers`
  // body containing just that one. Marks it resolved on success so it
  // disappears from the visible list.
  const recoverOne = async (ticker) => {
    setBusyTicker(ticker);
    setError(null);
    try {
      const r = await axios.post(
        `${API}/admin/recover-missing-dividend-tickers`,
        { tickers: [ticker] },
        { withCredentials: true, timeout: 90000 },
      );
      const okCount = r.data?.recovered_count || 0;
      const failCount = r.data?.failed_count || 0;
      if (okCount > 0) {
        toast.success(`Recovered ${ticker}`);
        setResolved((prev) => {
          const next = { recovered: new Set(prev.recovered), trashed: new Set(prev.trashed) };
          next.recovered.add(ticker);
          return next;
        });
      } else {
        const reason = r.data?.failed?.[0]?.reason || 'no_data';
        toast.error(`${ticker} could not be recovered`, { description: reason });
      }
      if (failCount > 0 && okCount === 0) {
        // no-op: toast already shown
      }
    } catch (e) {
      toast.error(`Failed to recover ${ticker}`, {
        description: e?.response?.data?.detail || e?.message || 'Unknown error',
      });
    } finally {
      setBusyTicker(null);
    }
  };

  // Trash a single ticker — hits the shared /admin/blocklist/{ticker}
  // endpoint which purges from all caches AND persists to the dynamic
  // blocklist so it never reappears in this modal or anywhere else.
  const trashOne = async (ticker) => {
    setBusyTicker(ticker);
    setError(null);
    try {
      const r = await axios.post(
        `${API}/admin/blocklist/${encodeURIComponent(ticker)}`,
        { reason: 'recover_missing_admin_trash' },
        { withCredentials: true },
      );
      toast.success(`Trashed ${ticker}`, {
        description: `Blocklist size: ${r.data?.blocklist_size ?? '—'}. Will not reappear in this scan.`,
        duration: 4000,
      });
      setResolved((prev) => {
        const next = { recovered: new Set(prev.recovered), trashed: new Set(prev.trashed) };
        next.trashed.add(ticker);
        return next;
      });
    } catch (e) {
      toast.error(`Failed to trash ${ticker}`, {
        description: e?.response?.data?.detail || e?.message || 'Unknown error',
      });
    } finally {
      setBusyTicker(null);
    }
  };

  const runRecovery = async () => {
    setRecovering(true);
    setError(null);
    // Only bulk-recover the tickers still visible (i.e. not already
    // recovered or trashed via the per-row buttons in this session).
    const targets = (dryRunResult?.missing || []).filter(
      (t) => !resolved.recovered.has(t) && !resolved.trashed.has(t)
    );
    const CHUNK = 25;
    const startingMissing = targets.length;
    const aggregated = {
      missing_count: startingMissing,
      recovered_count: 0,
      failed_count: 0,
      recovered: [],
      failed: [],
    };
    setRecoveryResult(aggregated);
    let safetyCounter = 0;
    try {
      let cursor = 0;
      while (safetyCounter < 200 && cursor < targets.length) {
        safetyCounter += 1;
        const chunk = targets.slice(cursor, cursor + CHUNK);
        const r = await axios.post(
          `${API}/admin/recover-missing-dividend-tickers`,
          { tickers: chunk },
          { withCredentials: true, timeout: 90000 },
        );
        const data = r.data || {};
        aggregated.recovered_count += data.recovered_count || 0;
        aggregated.failed_count += data.failed_count || 0;
        aggregated.recovered = [...aggregated.recovered, ...(data.recovered || [])];
        aggregated.failed = [...aggregated.failed, ...(data.failed || [])];
        setRecoveryResult({ ...aggregated });
        // Mark recovered tickers as resolved so they disappear from
        // the visible list as we go.
        if ((data.recovered || []).length > 0) {
          setResolved((prev) => {
            const next = { recovered: new Set(prev.recovered), trashed: new Set(prev.trashed) };
            (data.recovered || []).forEach((tk) => next.recovered.add(tk));
            return next;
          });
        }
        cursor += CHUNK;
        if ((data.recovered_count || 0) === 0 && (data.failed_count || 0) === 0) {
          // No progress this chunk — bail to avoid infinite loop.
          break;
        }
      }
      const total = aggregated.recovered_count;
      const f = aggregated.failed_count;
      if (total > 0) {
        toast.success(`Recovered ${total} ticker${total === 1 ? '' : 's'}${f ? ` · ${f} failed` : ''}`);
      } else {
        toast.info(`No tickers recovered${f ? ` (${f} failed)` : ''}`);
      }
    } catch (e) {
      const partial = aggregated.recovered_count;
      if (partial > 0) {
        setError(
          `Stopped after ${partial} successful recoveries — ${
            e?.response?.data?.detail || e?.message || 'request failed'
          }. Click "Recover all remaining" again to continue with what's left.`
        );
      } else {
        setError(e?.response?.data?.detail || e?.message || 'Recovery failed');
      }
    } finally {
      setRecovering(false);
    }
  };

  const close = () => {
    setOpen(false);
    setDryRunResult(null);
    setRecoveryResult(null);
    setError(null);
    setResolved({ recovered: new Set(), trashed: new Set() });
    setBusyTicker(null);
  };

  // Live-filtered list of tickers still awaiting a decision.
  const visibleMissing = (dryRunResult?.missing || []).filter(
    (t) => !resolved.recovered.has(t) && !resolved.trashed.has(t)
  );
  const visibleCount = visibleMissing.length;

  const missing = dryRunResult?.missing || [];
  const missingCount = dryRunResult?.missing_count ?? null;
  const universeSize = dryRunResult?.massive_universe ?? null;
  const cacheSize = dryRunResult?.stock_cache ?? null;

  // Round P111u — self-gate. If AuthContext already knows this isn't
  // an admin, render nothing regardless of parent state.
  if (user?.role !== 'admin') return null;

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        data-testid="admin-recover-missing-tickers-button"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-900 dark:text-emerald-200 border border-emerald-500/40 transition-colors"
      >
        <RefreshCw className="w-4 h-4" />
        Recover missing tickers
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={close}
          data-testid="admin-recover-missing-tickers-modal"
        >
          <div
            className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-card border border-emerald-500/40 rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-card border-b border-border/60">
              <div>
                <h2 className="text-sm font-bold">Recover missing dividend tickers</h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Re-inserts tickers from the Massive roster that are missing from stock_cache.
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                data-testid="admin-recover-missing-tickers-close"
                className="p-1 rounded hover:bg-muted/40 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {loading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Scanning Massive roster vs stock_cache…
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 p-3 rounded bg-rose-500/10 border border-rose-500/30 text-sm text-rose-200">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>{error}</div>
                </div>
              )}

              {dryRunResult && !error && (
                <>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-3 rounded bg-muted/30 border border-border/60">
                      <div className="text-[10px] uppercase text-muted-foreground">Massive roster</div>
                      <div className="text-lg font-bold">{universeSize?.toLocaleString() ?? '—'}</div>
                    </div>
                    <div className="p-3 rounded bg-muted/30 border border-border/60">
                      <div className="text-[10px] uppercase text-muted-foreground">stock_cache</div>
                      <div className="text-lg font-bold">{cacheSize?.toLocaleString() ?? '—'}</div>
                    </div>
                    <div className={`p-3 rounded border ${missingCount > 0 ? 'bg-amber-500/10 border-amber-500/40' : 'bg-emerald-500/10 border-emerald-500/40'}`}>
                      <div className="text-[10px] uppercase text-muted-foreground">Missing</div>
                      <div className={`text-lg font-bold ${missingCount > 0 ? 'text-amber-200' : 'text-emerald-200'}`}>
                        {missingCount?.toLocaleString() ?? '—'}
                      </div>
                    </div>
                  </div>

                  {missingCount === 0 && (
                    <div className="flex items-center gap-2 p-3 rounded bg-emerald-500/10 border border-emerald-500/40 text-sm text-emerald-200">
                      <CheckCircle2 className="w-4 h-4" />
                      stock_cache is in sync with the Massive roster. Nothing to recover.
                    </div>
                  )}

                  {missingCount > 0 && (
                    <>
                      <div className="border border-border/60 rounded-md overflow-hidden">
                        <div className="px-3 py-2 bg-muted/40 flex items-center justify-between">
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                            Missing tickers ({visibleCount} of {missingCount})
                          </span>
                          {(resolved.recovered.size > 0 || resolved.trashed.size > 0) && (
                            <span className="text-[10px] text-muted-foreground/80">
                              {resolved.recovered.size > 0 && (
                                <span className="text-emerald-300 mr-2">
                                  ↻ {resolved.recovered.size} recovered
                                </span>
                              )}
                              {resolved.trashed.size > 0 && (
                                <span className="text-rose-300">
                                  🗑 {resolved.trashed.size} trashed
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                        <div className="max-h-72 overflow-y-auto divide-y divide-border/40">
                          {visibleMissing.length === 0 ? (
                            <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                              All missing tickers resolved. Nothing left to review.
                            </div>
                          ) : (
                            visibleMissing.map((t) => (
                              <div
                                key={t}
                                className="px-3 py-1.5 flex items-center gap-3 text-xs"
                                data-testid={`recover-row-${t}`}
                              >
                                <span className="font-mono font-bold w-16 text-amber-200 shrink-0">{t}</span>
                                <span className="flex-1 text-muted-foreground/70 truncate">
                                  Missing from stock_cache. Choose: recover or permanently trash.
                                </span>
                                <button
                                  type="button"
                                  onClick={() => recoverOne(t)}
                                  disabled={busyTicker === t || recovering}
                                  className="text-muted-foreground hover:text-emerald-500 disabled:opacity-40 shrink-0"
                                  title={`Recover ${t} — re-runs the Massive + Yahoo fetch pipeline and inserts it back into stock_cache.`}
                                  data-testid={`recover-one-${t}`}
                                >
                                  {busyTicker === t
                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <RefreshCw className="w-3.5 h-3.5" />}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => trashOne(t)}
                                  disabled={busyTicker === t || recovering}
                                  className="text-muted-foreground hover:text-rose-500 disabled:opacity-40 shrink-0"
                                  title={`Trash ${t} — adds to permanent blocklist. Survives redeploys and future refreshes cannot re-insert it.`}
                                  data-testid={`trash-one-${t}`}
                                >
                                  {busyTicker === t
                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <Trash2 className="w-3.5 h-3.5" />}
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {!recoveryResult && visibleCount > 0 && (
                        <button
                          type="button"
                          onClick={runRecovery}
                          disabled={recovering || busyTicker !== null}
                          data-testid="admin-recover-missing-tickers-run"
                          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-100 border border-emerald-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {recovering ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Recovering {visibleCount} ticker{visibleCount === 1 ? '' : 's'} (25 at a time)…
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4" />
                              Recover all {visibleCount} remaining now
                            </>
                          )}
                        </button>
                      )}

                      {recoveryResult && (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-2 text-center">
                            <div className="p-3 rounded bg-emerald-500/10 border border-emerald-500/40">
                              <div className="text-[10px] uppercase text-muted-foreground">Recovered</div>
                              <div className="text-lg font-bold text-emerald-200">
                                {recoveryResult.recovered_count}
                              </div>
                            </div>
                            <div className={`p-3 rounded border ${recoveryResult.failed_count > 0 ? 'bg-rose-500/10 border-rose-500/40' : 'bg-muted/30 border-border/60'}`}>
                              <div className="text-[10px] uppercase text-muted-foreground">Failed</div>
                              <div className={`text-lg font-bold ${recoveryResult.failed_count > 0 ? 'text-rose-200' : 'text-muted-foreground'}`}>
                                {recoveryResult.failed_count}
                              </div>
                            </div>
                          </div>

                          {recoveryResult.recovered?.length > 0 && (
                            <div className="border border-emerald-500/30 rounded-md overflow-hidden">
                              <div className="px-3 py-2 bg-emerald-500/10 text-[10px] uppercase tracking-wide text-emerald-200">
                                Recovered ({recoveryResult.recovered.length})
                              </div>
                              <div className="p-3 max-h-32 overflow-y-auto text-xs font-mono text-emerald-100/80">
                                {recoveryResult.recovered.join(', ')}
                              </div>
                            </div>
                          )}

                          {recoveryResult.failed?.length > 0 && (
                            <div className="border border-rose-500/30 rounded-md overflow-hidden">
                              <div className="px-3 py-2 bg-rose-500/10 text-[10px] uppercase tracking-wide text-rose-200">
                                Failed ({recoveryResult.failed.length})
                              </div>
                              <div className="p-3 max-h-40 overflow-y-auto text-xs">
                                {recoveryResult.failed.map((f, i) => (
                                  <div key={i} className="py-0.5 text-rose-100/80">
                                    <span className="font-mono font-bold">{f.ticker}</span>
                                    <span className="text-rose-100/60"> — {f.reason}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
